import React from 'react';
import {
  DuckDbPartitionedTable,
  duckDbIdentifier,
  duckDbStringLiteral,
  fromArrowRow,
} from '@cotera/client/app/etc/duckdb';
import type {
  CoteraDuckDB,
  DuckDbCellValue,
  DuckDbWriteTarget,
} from '@cotera/client/app/etc/duckdb';
import { cn } from '@cotera/client/v0/lib/utils';
import { filterSelectsValue } from '@cotera/client/app/components/ui/data-grid';
import type {
  DataGridColumn,
  DataGridColumnStats,
  DataGridStructuredFilterValue,
} from '@cotera/client/app/components/ui/data-grid';
import type {
  DuckDbLayerGrid,
  DuckDbProjectedColumn,
  DuckDbProjection,
} from '@cotera/client/app/etc/duckdb/layers';
import { ResourceType } from '@cotera/api';
import type { ResourceUpdatedPayload } from '@cotera/api';
import {
  DATASET_TRIGGER_CRITERION_LABELS,
  type DatasetTriggerCriterion,
  type DatasetTriggerRunMode,
} from '@cotera/sdk/core';
import { ArtifactLink } from '../resources/navigation/artifact-link';
import { ArtifactResourceLink } from '../resources/navigation/artifact-resource-link';
import type { DatasetPreviewRow } from './preview';
import { Icon, Tooltip } from '../../components';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Skeleton } from '@cotera/client/v0/components/ui/skeleton';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import type { ReadonlyWatchable } from '@cotera/client/v0/actions/framework';

/** Resource kinds whose changes can rebind which automations watch a dataset. */
const AUTOMATION_WIRING_RESOURCE_TYPES: ReadonlySet<string> = new Set([
  'workflow',
  'workflow_trigger',
  'trigger',
]);

/**
 * Whether a `resource-updated` event can change what a dataset's automation
 * columns show.
 *
 * `resource-updated` is an org-wide channel, and the noisiest thing on it is
 * chat: streaming broadcasts one event per message delta. A dataset opened as
 * an artifact sits beside a streaming chat, so unfiltered every token the
 * assistant writes costs a wiring fetch, an overlay fetch and a DuckDB reload
 * — for a dataset none of it touched.
 *
 * Anything dropped here is still picked up by the reconcile cadence, so being
 * too strict costs a wiring change that lands late rather than one that never
 * lands at all.
 *
 * Only the bindings, deliberately — the dataset's *own* events are not here.
 * They fire on every write a run makes back into the dataset, and a reload is
 * the heaviest response available: two round trips, the whole overlay replaced
 * in DuckDB, and every loaded row repainted. The one row a run actually changed
 * already arrives on the run channel and is patched by itself, so reloading for
 * it repainted the entire grid to restate what one cell had just said. The two
 * dataset-shaped changes that do matter are covered elsewhere: a new artifact
 * re-points the source, which refreshes this on its own, and anything missed
 * waits for the reconcile.
 */
export const automationsAffectedBy = (
  payload: ResourceUpdatedPayload | undefined
): boolean => AUTOMATION_WIRING_RESOURCE_TYPES.has(payload?.resourceType ?? '');

/**
 * One table for every dataset, keyed by `dataset_id`, rather than one table
 * per dataset.
 *
 * The preview table is per-dataset because each dataset has a different
 * schema; this one's schema is fixed. A single table means two datasets open
 * at once (the dataset page plus a trigger picker) share it, and a live update
 * does not have to derive a table name from an event payload.
 *
 * Deliberately carries no primary key. It had one, to give
 * `ON CONFLICT DO UPDATE` a conflict target, and DuckDB refused to match a
 * composite key as one — then refused a delete-and-reinsert of the same key
 * too, which is the ART index limitation its error points at. Every attempt
 * threw inside the live handler and took the cell repaint with it.
 *
 * So uniqueness is enforced on read instead: {@link automationRunsSql} keeps
 * only the newest record per (row, workflow). A duplicate is then harmless
 * rather than fatal, which is the right trade for a local projection that a
 * full reload replaces wholesale.
 */
const DATASET_AUTOMATIONS_TABLE = 'dataset_automations';

const automationsTable = new DuckDbPartitionedTable({
  name: DATASET_AUTOMATIONS_TABLE,
  columns: [
    'dataset_id     TEXT NOT NULL',
    'dataset_row_id TEXT NOT NULL',
    'workflow_id    TEXT NOT NULL',
    'run_id         TEXT',
    'status         TEXT',
    'up_to_date     BOOLEAN',
    'ran_at         TIMESTAMP',
    'finished_at    TIMESTAMP',
    'outputs        JSON',
    // The run's final-node output, rendered server-side to text — the value the
    // expanded-workflow columns show. Distinct from `outputs`, which is reserved
    // for the columns a write-back node lays onto the row.
    'final_output   TEXT',
  ],
  partitionColumn: 'dataset_id',
});

/** The write order; the read side projects these back out by name. */
const AUTOMATION_COLUMNS = [
  'dataset_id',
  'dataset_row_id',
  'workflow_id',
  'run_id',
  'status',
  'up_to_date',
  'ran_at',
  'finished_at',
  'outputs',
  'final_output',
] as const;

/**
 * Height of the inline panel, from what it actually has to draw.
 *
 * The grid positions every row from one number, so this cannot be measured
 * after the fact — but it does not have to be a constant either. Every row of a
 * given dataset lists the same workflows, so counting them up front sizes the
 * panel exactly: a dataset with one automation reserved the same room as one
 * with four and spent most of it on empty space under a single line.
 *
 * Capped, because the panel scrolls: past the cap a long list is a scroll
 * rather than a row tall enough to push the rest of the grid off screen. The
 * cap is the height this used to be at all times, so this can only shrink a
 * row, never grow one.
 */
export const automationRowDetailHeight = (workflowCount: number): number => {
  /** `px-4 py-3` on the panel: 12px above the header, 12px below the last row. */
  const padding = 24;
  /** `gap-2`, once above each workflow row. */
  const gap = 8;
  /** The counts line, or the "Run all" button that sits beside it. */
  const header = 24;
  /** `size-6` run button + `py-2` + the 1px border top and bottom. */
  const workflowRow = 42;
  return Math.min(
    padding + header + workflowCount * (gap + workflowRow),
    AUTOMATION_ROW_DETAIL_MAX_HEIGHT
  );
};

export const AUTOMATION_ROW_DETAIL_MAX_HEIGHT = 232;

/**
 * What a workflow has done to a row, in the order a user cares about.
 *
 * `stale` is the mockup's "Out of date": the workflow completed, but the row
 * has changed since — which is a different thing from never having run.
 *
 * `cancelled` is kept apart from `failed` for the same reason the progress bar
 * keeps them apart: the user stopped it on purpose, and painting a row they
 * cancelled bright red sends them hunting a bug they caused themselves. It is
 * also the difference between a row that needs running again and one that is
 * broken — see {@link NEEDS_RUN_STATES}.
 *
 * `pending` is the one state no server ever reports: it is what this tab
 * writes for itself the moment a Run button is clicked, so the row says
 * something before the round trip that queues the run — and the broadcast that
 * reports it — have landed. It is superseded by whatever the server says the
 * moment either does, and withdrawn again if the request comes back empty.
 * See {@link markAutomationRunsPending}.
 */
type AutomationState =
  | 'succeeded'
  | 'running'
  | 'pending'
  | 'failed'
  | 'cancelled'
  | 'stale'
  | 'never';

/**
 * The status {@link markAutomationRunsPending} writes, mapped back to the
 * `pending` state by {@link STATE_CASE_SQL}.
 *
 * Deliberately not one of the server's run statuses: this has to be
 * distinguishable from `queued`, which means the server has a run, so that a
 * mark can be told apart from the thing it is standing in for.
 */
export const PENDING_RUN_STATUS = 'pending';

export const STATE_PRESENTATION: Record<
  AutomationState,
  { label: string; dot: string; chip: string }
> = {
  succeeded: {
    label: 'Succeeded',
    dot: 'bg-emerald-600',
    chip: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  },
  running: {
    label: 'Running',
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  },
  // Amber like `running`, because that is what it is on its way to being and a
  // colour change on confirmation would announce a transition nobody is waiting
  // on. The label and the pulse are what say the server has not answered yet.
  pending: {
    label: 'Pending',
    dot: 'bg-amber-400 animate-pulse',
    chip: 'bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  },
  failed: {
    label: 'Failed',
    dot: 'bg-rose-700',
    chip: 'bg-rose-50 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
  },
  // Legible as its own thing but deliberately not alarming: a stopped run is
  // outstanding work, not a fault.
  cancelled: {
    label: 'Cancelled',
    dot: 'bg-slate-400',
    chip: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  },
  stale: {
    label: 'Out of date',
    dot: 'bg-muted-foreground/60',
    chip: 'bg-muted text-muted-foreground',
  },
  never: {
    label: 'Never run',
    dot: 'bg-muted-foreground/30',
    chip: 'bg-muted text-muted-foreground',
  },
};

/** Column ids are index-based: workflow uuids are not safe identifiers. */
const stateColumnId = (index: number): string =>
  `__cotera_automation_wf${index}_state`;
const ranAtColumnId = (index: number): string =>
  `__cotera_automation_wf${index}_ran_at`;
const runIdColumnId = (index: number): string =>
  `__cotera_automation_wf${index}_run_id`;
/**
 * The passenger holding a workflow's final-node output for the row. Read by the
 * expanded-workflow grid column; rides along on the row like the state/ran_at
 * passengers rather than being a projected column of its own.
 */
const outputColumnId = (index: number): string =>
  `__cotera_automation_wf${index}_output`;

/** The grid column id for a workflow's expanded output column, by workflow id. */
export const workflowOutputGridColumnId = (workflowId: string): string =>
  `__cotera_automation_output_${workflowId}`;

/** Count of up-to-date workflows. A passenger, read by the cell renderer. */
const AUTOMATION_COUNT_COLUMN_ID = '__cotera_automation_runs';

/**
 * The row's overall state, and the column's actual value.
 *
 * A row can have several workflows in different states, so it is rolled up
 * worst-first: anything failed makes the row failed, then running, then out of
 * date, then not-yet-complete, and only a row where every workflow has
 * succeeded on current data reads as succeeded.
 *
 * Being a single category per row is what makes the column filterable and
 * sortable through the grid's ordinary machinery — a count could only ever be
 * filtered by number, which is not a question anyone asks of it.
 */
export const AUTOMATION_SUMMARY_COLUMN_ID = '__cotera_automation_state';

export type DatasetAutomationWorkflow = {
  id: string;
  name: string;
  triggerId: string;
  /**
   * Dataset columns this workflow writes back, by name.
   *
   * Carried so a bulk-run control can say what a workflow will change before it
   * is started — "writes CONTEXT, LATENCY" is the difference between picking
   * workflows by name and picking them by effect.
   */
  writes: string[];
  /**
   * Whether the workflow's trigger fires on its own.
   *
   * Carried for the same reason as {@link DatasetAutomationWorkflow.writes}: the
   * Run menu is the *only* way a `manual` workflow ever runs, and a user
   * deciding what to start should be able to see which of these have been
   * running all along and which have been waiting for them.
   */
  runMode: DatasetTriggerRunMode;
  /**
   * What the workflow's dataset trigger watches for — its cadence in the
   * dataset-trigger sense ("a new row is added", "values change"). Null when the
   * criterion on the wire is one this build doesn't recognise, so the cadence
   * line is omitted rather than guessed at.
   */
  criterion: DatasetTriggerCriterion | null;
  /**
   * Whether this reader may start the workflow, or only watch it run.
   *
   * False on a dataset shared out of another org, for that org's own
   * automations: their run state is part of reading the row, but firing them is
   * refused server-side and the workflow behind one is not openable either. The
   * column and its states still render — only the controls drop out.
   */
  canRun: boolean;
};

/**
 * The automations this reader can actually start.
 *
 * Every run control narrows to this before drawing itself: the Run menu in the
 * column header works from it, and the row panel's "Run all" only appears when
 * it is the whole list. Unshared, it is the whole list — the two only diverge
 * on a dataset shared out of another org, whose automations belong to the
 * sharer and are refused server-side.
 */
export const runnableWorkflows = (
  workflows: readonly DatasetAutomationWorkflow[]
): DatasetAutomationWorkflow[] =>
  workflows.filter((workflow) => workflow.canRun);

const readState = (row: DatasetPreviewRow, index: number): AutomationState => {
  const value = row[stateColumnId(index)];
  return typeof value === 'string' && value in STATE_PRESENTATION
    ? (value as AutomationState)
    : 'never';
};

/**
 * A grid row's text field, where an empty string means "absent".
 *
 * Deliberately not `readString` from `etc/duckdb/arrow`: that one normalises an
 * aggregate row and keeps `''` as a value, which is right there and wrong here —
 * a run id is either present or it is not, and `''` would render an empty link.
 */
const readRowString = (row: DatasetPreviewRow, key: string): string | null => {
  const value = row[key];
  return typeof value === 'string' && value !== '' ? value : null;
};

/** Coarse relative time; the run detail page is where exact times live. */
const relativeTime = (value: unknown): string | null => {
  const time =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
      ? Date.parse(value)
      : Number.NaN;
  if (!Number.isFinite(time)) {
    return null;
  }
  const minutes = Math.max(0, Math.round((Date.now() - time) / 60_000));
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
};

/**
 * Collapses a run into one of the five states, in SQL so that sorting and
 * filtering see the same value the cell renders.
 *
 * Statuses come from `workflow_runs`: queued/incomplete are in flight,
 * cancelled/stopped were ended on purpose, and anything else that is not
 * `completed` has stopped for a bad reason.
 *
 * The cancelled arm has to sit above the catch-all, and its statuses match the
 * ones the server's group tally counts as cancelled — the bar and the cells
 * describe the same runs, so classifying them differently would have a row read
 * as failed while the strip beneath it called the same run cancelled.
 *
 * The pending arm is this tab's own mark rather than anything from
 * `workflow_runs`, and is first because the `<> 'completed'` catch-all below
 * would otherwise paint a run the user has only just asked for bright red.
 */
const STATE_CASE_SQL = `CASE
  WHEN status = ${duckDbStringLiteral(PENDING_RUN_STATUS)} THEN 'pending'
  WHEN status IN ('queued', 'incomplete') THEN 'running'
  WHEN status IN ('cancelled', 'stopped') THEN 'cancelled'
  WHEN status <> 'completed' THEN 'failed'
  WHEN up_to_date IS FALSE THEN 'stale'
  ELSE 'succeeded'
END`;

/**
 * The aggregate columns every overlay read groups into, per dataset row.
 *
 * Shared because the page query's JOIN and the live re-read both project the
 * *same* `selectExpressions` over the result, so a column present in one and
 * missing from the other is a binder error at runtime — which is exactly what
 * happened when the state rollup's counts were added to only one of them.
 */
/**
 * The per-(row, workflow) records an overlay read aggregates over.
 *
 * `DISTINCT ON` keeps only the newest record for each pair. The table has no
 * primary key — see its declaration — so a live update appends rather than
 * replacing, and without this a row that has run twice would be counted twice.
 * Ordered by `ran_at` descending so the surviving record is the current one.
 *
 * A pending mark outranks `ran_at` outright, and can do so safely because it is
 * always the newest record for its pair: the only two things that write
 * alongside it — a live update and an overlay reload — both clear every record
 * for the pair first. Ranking it explicitly rather than leaning on its own
 * timestamp is what keeps the mark visible whatever DuckDB's session clock
 * makes of `now()` against the server timestamps beside it.
 */
const automationRunsSql = (datasetId: string, rowFilterSql = ''): string =>
  `SELECT DISTINCT ON (dataset_row_id, workflow_id)
          dataset_row_id, workflow_id, run_id, ran_at, final_output,
          ${STATE_CASE_SQL} AS state
   FROM ${DATASET_AUTOMATIONS_TABLE}
   WHERE dataset_id = ${duckDbStringLiteral(datasetId)}${rowFilterSql}
   ORDER BY dataset_row_id, workflow_id,
            (status = ${duckDbStringLiteral(PENDING_RUN_STATUS)}) DESC,
            ran_at DESC`;

const automationAggregateSql = (
  workflows: readonly DatasetAutomationWorkflow[]
): string => {
  const perWorkflow = workflows.flatMap((workflow, index) => {
    const match = `FILTER (WHERE workflow_id = ${duckDbStringLiteral(
      workflow.id
    )})`;
    return [
      `any_value(state) ${match} AS ${duckDbIdentifier(stateColumnId(index))}`,
      `any_value(ran_at) ${match} AS ${duckDbIdentifier(ranAtColumnId(index))}`,
      `any_value(run_id) ${match} AS ${duckDbIdentifier(runIdColumnId(index))}`,
      `any_value(final_output) ${match} AS ${duckDbIdentifier(
        outputColumnId(index)
      )}`,
    ];
  });
  return [
    `count(*) FILTER (WHERE state = 'succeeded') AS up_to_date_count`,
    `count(*) FILTER (WHERE state = 'failed') AS failed_count`,
    `count(*) FILTER (WHERE state = 'running') AS running_count`,
    `count(*) FILTER (WHERE state = 'pending') AS pending_count`,
    `count(*) FILTER (WHERE state = 'cancelled') AS cancelled_count`,
    `count(*) FILTER (WHERE state = 'stale') AS stale_count`,
    ...perWorkflow,
  ].join(', ');
};

/**
 * The per-row aggregate columns, projected off whichever alias holds them.
 *
 * Parameterised by alias because the same list is projected twice: over the
 * page query's LEFT JOIN, and over the CTE the live re-read builds. A column
 * present in one and missing from the other is a binder error at runtime, so
 * there is exactly one definition and both callers use it.
 */
const automationSelectExpressions = (
  runsAlias: string,
  workflows: readonly DatasetAutomationWorkflow[]
): string[] => [
  `COALESCE(${runsAlias}.up_to_date_count, 0) AS ${duckDbIdentifier(
    AUTOMATION_COUNT_COLUMN_ID
  )}`,
  // Worst-first rollup, so one value can stand for a row whose workflows
  // disagree. A row with nothing joined has never run; a row that is short
  // of a full house of successes is not done yet.
  //
  // `cancelled` outranks `stale` because both mean the row is owed a run and
  // cancelled says why; it sits below `failed` and `running` because a fault or
  // work still in flight is the more urgent thing to see.
  //
  // `pending` sits directly under `running` — both are work in flight, and a
  // row with one of each has more to say about the run the server has already
  // confirmed. Above everything below it, so clicking Run on a stale row moves
  // the pinned cell immediately rather than at the end of the round trip, which
  // is the whole point of the mark.
  `CASE
     WHEN COALESCE(${runsAlias}.failed_count, 0) > 0 THEN 'failed'
     WHEN COALESCE(${runsAlias}.running_count, 0) > 0 THEN 'running'
     WHEN COALESCE(${runsAlias}.pending_count, 0) > 0 THEN 'pending'
     WHEN COALESCE(${runsAlias}.cancelled_count, 0) > 0 THEN 'cancelled'
     WHEN COALESCE(${runsAlias}.stale_count, 0) > 0 THEN 'stale'
     WHEN COALESCE(${runsAlias}.up_to_date_count, 0) >= ${
    workflows.length
  } THEN 'succeeded'
     ELSE 'never'
   END AS ${duckDbIdentifier(AUTOMATION_SUMMARY_COLUMN_ID)}`,
  ...workflows.flatMap((_workflow, index) => [
    // A missing join row means the workflow has never run for this row, so
    // the default is a real state rather than a null.
    `COALESCE(${runsAlias}.${duckDbIdentifier(
      stateColumnId(index)
    )}, 'never') AS ${duckDbIdentifier(stateColumnId(index))}`,
    `${runsAlias}.${duckDbIdentifier(
      ranAtColumnId(index)
    )} AS ${duckDbIdentifier(ranAtColumnId(index))}`,
    `${runsAlias}.${duckDbIdentifier(
      runIdColumnId(index)
    )} AS ${duckDbIdentifier(runIdColumnId(index))}`,
    `${runsAlias}.${duckDbIdentifier(
      outputColumnId(index)
    )} AS ${duckDbIdentifier(outputColumnId(index))}`,
  ]),
];

/** Alias the live re-read's CTE uses. Fixed: it builds its own query. */
const LIVE_RUNS_ALIAS = 'cotera_automations';

/**
 * The automations layer's projection: a LEFT JOIN onto the per-row aggregate,
 * plus one pivoted set of columns per bound workflow.
 *
 * Aggregating with `FILTER` rather than `max(CASE ...)` because there is
 * exactly one record per (row, workflow) — `any_value` says that, where `max`
 * would silently pick alphabetically if the grain ever broke.
 */
export const buildAutomationProjection = ({
  datasetId,
  rowIdColumn,
  workflows,
  baseAlias,
  runsAlias,
  loadSummaryStats,
}: {
  datasetId: string;
  rowIdColumn: string;
  workflows: readonly DatasetAutomationWorkflow[];
  /** The source this layer wraps; base columns must be qualified with it. */
  baseAlias: string;
  /** Unique across the stack, so two automation layers cannot collide. */
  runsAlias: string;
  /**
   * How to summarise the Workflows column in its header. Taken here rather
   * than matched on a column id later, so this function is the single place
   * that describes that column: its name, its type, and how to read it.
   */
  loadSummaryStats?: DuckDbProjectedColumn['loadStats'];
}): DuckDbProjection => {
  // No workflows watch this dataset yet. The column still renders so its header
  // can host "Add automation", but there is nothing to join onto — and the runs
  // table is never loaded in this case, so a join would query a table that does
  // not exist. Project a constant instead and let the cells render empty.
  if (workflows.length === 0) {
    return {
      selectExpressions: [
        `NULL AS ${duckDbIdentifier(AUTOMATION_SUMMARY_COLUMN_ID)}`,
      ],
      joins: [],
      columns: [
        {
          name: AUTOMATION_SUMMARY_COLUMN_ID,
          type: 'VARCHAR',
          ...(loadSummaryStats !== undefined && {
            loadStats: loadSummaryStats,
          }),
        },
      ],
    };
  }
  return {
    selectExpressions: automationSelectExpressions(runsAlias, workflows),
    joins: [
      `LEFT JOIN (
    SELECT dataset_row_id, ${automationAggregateSql(workflows)}
    FROM (${automationRunsSql(datasetId)})
    GROUP BY dataset_row_id
  ) AS ${runsAlias}
    ON ${runsAlias}.dataset_row_id = CAST(${baseAlias}.${duckDbIdentifier(
        rowIdColumn
      )} AS VARCHAR)`,
    ],
    // Only the state is a grid column, so only it is declared sortable and
    // filterable. The count and the per-workflow state/ran_at/run_id values
    // ride along on the row for the cell and the detail panel to read.
    columns: [
      {
        name: AUTOMATION_SUMMARY_COLUMN_ID,
        type: 'VARCHAR',
        ...(loadSummaryStats !== undefined && { loadStats: loadSummaryStats }),
      },
    ],
  };
};

/**
 * Applies one run's new state to the browser's copy.
 *
 * This is the half that keeps *querying* accurate: the next sort, filter or
 * header-stat query reads the table, so once this lands there is nothing to
 * refetch. Repainting the visible cell is a separate, cheaper step.
 */
export const upsertAutomationRun = async (
  db: DuckDbWriteTarget,
  params: {
    datasetId: string;
    datasetRowId: string;
    workflowId: string;
    runId: string;
    status: string;
    finishedAt: string | null;
  }
): Promise<void> => {
  const literal = duckDbStringLiteral;
  // `up_to_date` is hardcoded true: a run that just fired is by definition
  // current for the row as it stands.
  await automationsTable.upsert(db, {
    where: `dataset_id = ${literal(params.datasetId)}
       AND dataset_row_id = ${literal(params.datasetRowId)}
       AND workflow_id = ${literal(params.workflowId)}`,
    columns: AUTOMATION_COLUMNS,
    values: [
      literal(params.datasetId),
      literal(params.datasetRowId),
      literal(params.workflowId),
      literal(params.runId),
      literal(params.status),
      'true',
      'now()',
      params.finishedAt === null
        ? 'NULL'
        : `CAST(${literal(params.finishedAt)} AS TIMESTAMP)`,
      'NULL',
      // The live broadcast carries no final output; it fills in on the next
      // overlay reload. Nulled here rather than left stale, since this record
      // replaces a different run than the one whose output was showing.
      'NULL',
    ],
  });
};

/** Addresses one row's records for a set of workflows. */
const automationRunMatchSql = (params: {
  datasetId: string;
  datasetRowId: string;
  workflowIds: readonly string[];
}): string =>
  `dataset_id = ${duckDbStringLiteral(params.datasetId)}
     AND dataset_row_id = ${duckDbStringLiteral(params.datasetRowId)}
     AND workflow_id IN (${params.workflowIds
       .map(duckDbStringLiteral)
       .join(', ')})`;

/**
 * Records that this tab has asked for these runs, before it has been told they
 * started.
 *
 * A click on Run is two waits, not one: the request that queues the run, and
 * then the broadcast that says it is running. Until both land there is nothing
 * in the browser's copy to paint from, so the pill kept its old state and the
 * button read as dead — which is exactly when a user clicks it again.
 *
 * Appended rather than upserted, and this is the difference that makes the mark
 * safe to withdraw: the record it stands in front of is left underneath it, so
 * {@link clearAutomationRunsPending} is a delete and the row goes back to what
 * it said before — its last run and that run's id — rather than to never-run.
 * {@link automationRunsSql} is what puts the mark on top.
 */
export const markAutomationRunsPending = async (
  db: DuckDbWriteTarget,
  params: {
    datasetId: string;
    datasetRowId: string;
    workflowIds: readonly string[];
  }
): Promise<void> => {
  if (params.workflowIds.length === 0) {
    return;
  }
  const literal = duckDbStringLiteral;
  await automationsTable.write(db, async (writer) => {
    // The table is created by whichever of the overlay load and this gets there
    // first, and on a row run before the overlay lands it is this.
    await writer.ensure();
    // A second click replaces this tab's own mark rather than stacking another
    // on top of it: two clicks are one outstanding request to look at, and the
    // records left over would each have to be found again to withdraw it.
    await writer.deleteWhere(
      `${automationRunMatchSql(params)} AND status = ${literal(
        PENDING_RUN_STATUS
      )}`
    );
    for (const workflowId of params.workflowIds) {
      await writer.insert({
        columns: AUTOMATION_COLUMNS,
        values: [
          literal(params.datasetId),
          literal(params.datasetRowId),
          literal(workflowId),
          // No run id yet — learning it is what the round trip is for. The
          // panel renders the gap rather than a link that goes nowhere.
          'NULL',
          literal(PENDING_RUN_STATUS),
          // Same reasoning as the live upsert: a run being asked for now is by
          // definition current for the row as it stands.
          'true',
          'now()',
          'NULL',
          'NULL',
          // No output until the run has actually produced one.
          'NULL',
        ],
      });
    }
  });
};

/**
 * Withdraws marks {@link markAutomationRunsPending} left, for a request that
 * turned out to start nothing.
 *
 * Scoped to the pending status so this can only ever remove the tab's own
 * marks — a run that started in the meantime is the server's record and is not
 * this function's to delete.
 */
export const clearAutomationRunsPending = async (
  db: DuckDbWriteTarget,
  params: {
    datasetId: string;
    datasetRowId: string;
    workflowIds: readonly string[];
  }
): Promise<void> => {
  if (params.workflowIds.length === 0) {
    return;
  }
  await automationsTable.write(db, async (writer) => {
    await writer.ensure();
    await writer.deleteWhere(
      `${automationRunMatchSql(params)} AND status = ${duckDbStringLiteral(
        PENDING_RUN_STATUS
      )}`
    );
  });
};

/**
 * The bulk counterpart to {@link markAutomationRunsPending}: paints a whole set
 * of rows pending for a set of workflows in one write.
 *
 * Used by the "Run" scopes that dispatch many rows at once — a filtered/needs-run
 * batch, and the "all rows" replay — so the grid acknowledges the click on every
 * row in view the instant it is made, rather than sitting unchanged until the
 * first run's broadcast lands. Like the single-row mark it is optimistic and
 * self-correcting: the live per-run events supersede each cell as its run starts,
 * and the next overlay reload replaces the whole table, dropping any mark whose
 * row the dispatch turned out not to run.
 *
 * One multi-row INSERT rather than a call per row: a page is a couple of hundred
 * rows and a replay can select several workflows, and per-row round trips through
 * the writer would be thousands of statements under one lock.
 */
export const markAutomationRunsPendingForRows = async (
  db: DuckDbWriteTarget,
  params: {
    datasetId: string;
    datasetRowIds: readonly string[];
    workflowIds: readonly string[];
  }
): Promise<void> => {
  if (params.datasetRowIds.length === 0 || params.workflowIds.length === 0) {
    return;
  }
  const literal = duckDbStringLiteral;
  await automationsTable.write(db, async (writer) => {
    await writer.ensure();
    const rowList = params.datasetRowIds.map(literal).join(', ');
    const workflowList = params.workflowIds.map(literal).join(', ');
    // Replace only this tab's own outstanding marks for these pairs, same as the
    // single-row path: two clicks on Start are one request to look at.
    await writer.deleteWhere(
      `dataset_id = ${literal(params.datasetId)}
         AND dataset_row_id IN (${rowList})
         AND workflow_id IN (${workflowList})
         AND status = ${literal(PENDING_RUN_STATUS)}`
    );
    const tuples: string[] = [];
    for (const rowId of params.datasetRowIds) {
      for (const workflowId of params.workflowIds) {
        tuples.push(
          // Column order matches AUTOMATION_COLUMNS: dataset_id, dataset_row_id,
          // workflow_id, run_id (none yet), status, up_to_date, ran_at,
          // finished_at, outputs, final_output.
          `(${literal(params.datasetId)}, ${literal(rowId)}, ${literal(
            workflowId
          )}, NULL, ${literal(
            PENDING_RUN_STATUS
          )}, true, now(), NULL, NULL, NULL)`
        );
      }
    }
    await writer.exec(
      `INSERT INTO ${DATASET_AUTOMATIONS_TABLE} (${AUTOMATION_COLUMNS.join(
        ', '
      )}) VALUES ${tuples.join(', ')}`
    );
  });
};

/**
 * Creates the runs table, empty, if nothing has put anything in it yet.
 *
 * The projection above joins this table from the moment a workflow is bound,
 * and the wiring that turns that join on is published before the overlay it
 * joins — deliberately, so the column paints in one round trip rather than
 * three. What that trades on is an empty table reading as "never run", which is
 * only true of a table that *exists*: nothing creates this one until an overlay
 * or a live run writes to it, so on a dataset whose first automation was
 * attached in this session the grid's next query was a catalog error instead —
 * and since a reload repaints loaded rows rather than re-querying, the error
 * stayed on screen until something else happened to re-run the query.
 */
export const ensureAutomationsTable = async (
  db: DuckDbWriteTarget
): Promise<void> => {
  await automationsTable.ensure(db);
};

/** Replaces one dataset's slice of the shared table. */
export const loadAutomationOverlay = async (
  db: DuckDbWriteTarget,
  params: {
    datasetId: string;
    rows: readonly Record<string, unknown>[];
  }
): Promise<void> => {
  await automationsTable.replacePartition(db, params.datasetId, {
    from: 'rows',
    rows: params.rows,
    columns: AUTOMATION_COLUMNS,
    // The payload is camelCase and the table is snake_case, and the two
    // timestamps arrive as ISO strings that DuckDB will not infer.
    select: [
      duckDbStringLiteral(params.datasetId),
      'datasetRowId',
      'workflowId',
      'runId',
      'status',
      'upToDate',
      'CAST(ranAt AS TIMESTAMP)',
      'CAST(finishedAt AS TIMESTAMP)',
      'outputs',
      'finalOutput',
    ],
    // Legacy or deleted row keys have nothing to join against.
    where: 'datasetRowId IS NOT NULL',
  });
};

/**
 * Fixed order for the breakdown: best outcome first, never-run last.
 *
 * The reverse of the rollup's severity order, so the header reads in the
 * opposite direction to the one a row is collapsed by.
 */
const STATE_ORDER: readonly AutomationState[] = [
  'succeeded',
  'stale',
  'cancelled',
  'pending',
  'running',
  'failed',
  'never',
];

/**
 * The run-state breakdown behind the Workflows header.
 *
 * The column's value is a count of up-to-date workflows, so the generic
 * numeric path renders the distribution of a small integer — `μ 0` over an
 * axis from 0 to 0. What is worth knowing is the share of work in each state,
 * which means counting (row × workflow) pairs rather than rows: a row with one
 * failed and one succeeded workflow contributes to both.
 *
 * Unpivots the per-workflow state columns the join already puts on every row,
 * so the numbers come from the same expression the cells render from and
 * cannot disagree with them. Never-run pairs are `'never'` rather than null
 * (the `COALESCE` in `automationSelectExpressions`), so they are counted rather
 * than dropped.
 */
export const loadAutomationStateBreakdown = async (
  db: Pick<CoteraDuckDB, 'rawQuery'>,
  params: {
    sourceSql: string;
    workflows: readonly DatasetAutomationWorkflow[];
  }
): Promise<DataGridColumnStats> => {
  if (params.workflows.length === 0) {
    return { kind: 'categorical', buckets: [], uniqueCount: 0 };
  }

  // Counts rows, not (row x workflow) pairs, because the column holds one
  // rolled-up state per row. Anything else and clicking a mark would filter to
  // a different population than the mark reported.
  const result = await db.rawQuery(
    `SELECT ${duckDbIdentifier(AUTOMATION_SUMMARY_COLUMN_ID)} AS state,
            count(*) AS state_count
     FROM ${params.sourceSql} AS automation_source
     GROUP BY ${duckDbIdentifier(AUTOMATION_SUMMARY_COLUMN_ID)}`
  );
  const counts = new Map<string, number>();
  for (const raw of result.toArray()) {
    const row = fromArrowRow(raw) as {
      state?: string;
      state_count?: number | string;
    };
    if (typeof row.state === 'string') {
      counts.set(row.state, Number(row.state_count ?? 0));
    }
  }

  const total = [...counts.values()].reduce((sum, count) => sum + count, 0);
  return {
    kind: 'categorical',
    // Every state is present even at zero, so the header keeps a stable shape
    // as runs move between them rather than reflowing on each update.
    buckets: STATE_ORDER.map((state) => {
      const count = counts.get(state) ?? 0;
      return {
        key: state,
        label: STATE_PRESENTATION[state].label,
        count,
        share: total === 0 ? 0 : count / total,
        // Clicking a mark filters the grid to that state. Sound only because
        // the column's value *is* the state: the filter the grid builds from
        // this targets the same column the bucket was counted from.
        filter: { kind: 'in' as const, values: [state] },
      };
    }),
    uniqueCount: STATE_ORDER.length,
  };
};

/** What a row with no runs at all must be painted as. */
const neverRunValues = (
  workflows: readonly DatasetAutomationWorkflow[]
): Record<string, DuckDbCellValue> =>
  Object.fromEntries([
    [AUTOMATION_COUNT_COLUMN_ID, 0],
    [AUTOMATION_SUMMARY_COLUMN_ID, 'never'],
    ...workflows.flatMap((_workflow, index) => [
      [stateColumnId(index), 'never'],
      [ranAtColumnId(index), null],
      [runIdColumnId(index), null],
      [outputColumnId(index), null],
    ]),
  ]);

/**
 * Re-reads the aggregate for a set of rows, returning the same column values
 * the page query's join would have produced for each.
 *
 * Derived from the same table and the same state expression as the join, so a
 * patched cell cannot disagree with what a later sort or filter sees.
 *
 * Rows absent from the table are returned as never-run rather than omitted:
 * an overlay can shrink (a run's dataset row leaves the artifact), and a
 * caller repainting must be able to clear a stale value, not just overwrite
 * one.
 */
export const selectOverlayValuesForRows = async (
  db: DuckDbWriteTarget,
  params: {
    datasetId: string;
    datasetRowIds: readonly string[];
    workflows: readonly DatasetAutomationWorkflow[];
  }
): Promise<Map<string, Record<string, DuckDbCellValue>>> => {
  const defaults = neverRunValues(params.workflows);
  const results = new Map<string, Record<string, DuckDbCellValue>>(
    params.datasetRowIds.map((rowId) => [rowId, { ...defaults }])
  );
  if (params.datasetRowIds.length === 0) {
    return results;
  }

  const selectExpressions = automationSelectExpressions(
    LIVE_RUNS_ALIAS,
    params.workflows
  );
  const idList = params.datasetRowIds.map(duckDbStringLiteral).join(', ');

  const result = await db.rawQuery(
    // Same aggregate list as the page query's join, because the same
    // `selectExpressions` are projected over both.
    `WITH ${LIVE_RUNS_ALIAS} AS (
       SELECT dataset_row_id, ${automationAggregateSql(params.workflows)}
       FROM (${automationRunsSql(
         params.datasetId,
         ` AND dataset_row_id IN (${idList})`
       )})
       GROUP BY dataset_row_id
     )
     SELECT ${LIVE_RUNS_ALIAS}.dataset_row_id, ${selectExpressions.join(', ')}
     FROM ${LIVE_RUNS_ALIAS}`
  );

  for (const raw of result.toArray()) {
    const row = fromArrowRow(raw);
    const rowId = row['dataset_row_id'];
    if (typeof rowId !== 'string') {
      continue;
    }
    // The join key is how the row was addressed, not a value to paint with.
    const { dataset_row_id: _key, ...values } = row;
    results.set(rowId, values);
  }
  return results;
};

/**
 * Repaints every loaded row from the table's current contents.
 *
 * The counterpart to a full reload: `loadAutomationOverlay` replaces what
 * DuckDB holds, and this makes the rows on screen agree with it. Bounded by
 * what is loaded, not by the dataset — a page is a couple of hundred rows.
 */
export const repaintLoadedRows = async (
  db: DuckDbWriteTarget,
  params: {
    datasetId: string;
    workflows: readonly DatasetAutomationWorkflow[];
    grid: DuckDbLayerGrid | null;
  }
): Promise<void> => {
  const grid = params.grid;
  if (grid === null) {
    return;
  }
  const rowIds = grid.loadedRowIds();
  if (rowIds.length === 0) {
    return;
  }
  // One query for every loaded row rather than one per row: the same
  // aggregate the join computes, restricted to the ids on screen.
  const values = await selectOverlayValuesForRows(db, {
    datasetId: params.datasetId,
    datasetRowIds: rowIds.map((rowId) => String(rowId)),
    workflows: params.workflows,
  });
  for (const rowId of rowIds) {
    const row = values.get(String(rowId));
    if (row !== undefined) {
      grid.patch(rowId, row);
    }
  }
};

const StatePill: React.FC<{ state: AutomationState }> = ({ state }) => {
  const presentation = STATE_PRESENTATION[state];
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[11px] font-medium',
        presentation.chip
      )}
    >
      {presentation.label}
    </span>
  );
};

/**
 * The `2/4` chip plus the per-workflow tick strip.
 *
 * Both read from the row rather than issuing their own query — the join has
 * already put every workflow's state on it.
 */
/**
 * Percentage breakdown of run states.
 *
 * Rendered here rather than through the grid's `CategoricalChart` because that
 * prints raw counts and ignores `share`, and a percentage is the thing worth
 * reading. Reuses the cells' colours so the header speaks the same vocabulary
 * as the rows beneath it.
 */
const StateBreakdown: React.FC<{
  stats: DataGridColumnStats | null;
  /** Current filter on this column, so the selected state reads as selected. */
  filterValue: unknown;
  onSelect: (value: DataGridStructuredFilterValue) => void;
}> = ({ stats, filterValue, onSelect }) => {
  if (stats === null || stats.kind === 'loading') {
    return <span className="text-[10px] text-muted-foreground">Loading…</span>;
  }
  // Shown rather than returned as null: the header treats null as "render
  // nothing", so a failed query would be indistinguishable from a column that
  // simply has no stats — which is exactly how this bug first presented.
  if (stats.kind === 'error') {
    return (
      <span className="text-[10px] text-destructive">
        {stats.message ?? 'Could not load run states'}
      </span>
    );
  }
  if (stats.kind !== 'categorical') {
    return null;
  }
  const total = stats.buckets.reduce((sum, bucket) => sum + bucket.count, 0);
  if (total === 0) {
    return (
      <span className="text-[10px] text-muted-foreground">No runs yet</span>
    );
  }

  return (
    <div className="space-y-[3px]">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {total.toLocaleString()} of {stats.buckets.length} states
      </div>
      {/* One stacked bar first, so the shape reads at a glance before any
          individual number does. */}
      <div className="flex h-1.5 overflow-hidden rounded-full bg-muted">
        {stats.buckets
          .filter((bucket) => bucket.count > 0)
          .map((bucket) => (
            <span
              key={bucket.key}
              className={cn(
                'h-full',
                STATE_PRESENTATION[bucket.key as AutomationState]?.dot ??
                  'bg-muted'
              )}
              style={{ width: `${((bucket.share ?? 0) * 100).toFixed(2)}%` }}
            />
          ))}
      </div>
      {stats.buckets
        .filter((bucket) => bucket.count > 0)
        .map((bucket) => {
          const selected = filterSelectsValue(filterValue, bucket.filter);
          return (
            // A button, not a row of text: overriding `renderHeaderStats`
            // replaces the grid's own chart wholesale, including the click
            // handling that turns a mark into a filter, so it has to be wired
            // back up here.
            <button
              key={bucket.key}
              type="button"
              aria-pressed={selected}
              className={cn(
                'flex w-full items-center gap-1.5 rounded px-0.5 text-left hover:bg-muted',
                selected && 'bg-muted'
              )}
              onClick={() => {
                if (bucket.filter !== undefined) {
                  onSelect(bucket.filter);
                }
              }}
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 shrink-0 rounded-full',
                  STATE_PRESENTATION[bucket.key as AutomationState]?.dot ??
                    'bg-muted'
                )}
              />
              <span
                className={cn(
                  'min-w-0 flex-1 truncate text-[10px]',
                  selected ? 'text-foreground' : 'text-foreground/80'
                )}
              >
                {bucket.label}
              </span>
              <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                {Math.round((bucket.share ?? 0) * 100)}%
              </span>
            </button>
          );
        })}
    </div>
  );
};

/**
 * The rolled-up count chip and per-workflow tick strip for one row — the ready
 * state of the Automations summary cell. Split out from {@link
 * AutomationSummaryCell} so the loading path can render a shimmer of the same
 * shape without either branch touching a hook the other does not.
 */
const AutomationSummaryContent: React.FC<{
  row: DatasetPreviewRow;
  workflows: readonly DatasetAutomationWorkflow[];
}> = ({ row, workflows }) => {
  const upToDate = Number(row[AUTOMATION_COUNT_COLUMN_ID] ?? 0);
  const allSucceeded = upToDate === workflows.length && workflows.length > 0;
  return (
    <span className="flex items-center gap-1.5">
      <span
        className={cn(
          'flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] tabular-nums',
          allSucceeded
            ? STATE_PRESENTATION.succeeded.chip
            : 'bg-muted text-muted-foreground'
        )}
      >
        <span
          className={cn(
            'h-1.5 w-1.5 rounded-full',
            allSucceeded
              ? STATE_PRESENTATION.succeeded.dot
              : STATE_PRESENTATION.never.dot
          )}
        />
        {upToDate}/{workflows.length}
      </span>
      <span className="flex items-center gap-px">
        {workflows.map((workflow, index) => (
          <span
            key={workflow.id}
            title={`${workflow.name}: ${
              STATE_PRESENTATION[readState(row, index)].label
            }`}
            className={cn(
              'h-3 w-1 rounded-[1px]',
              STATE_PRESENTATION[readState(row, index)].dot
            )}
          />
        ))}
      </span>
    </span>
  );
};

/**
 * Shimmering stand-in for {@link AutomationSummaryContent}, shaped like the
 * count chip and one bar per bound workflow. Shown in a row's Automations cell
 * while the per-row overlay is still loading: the column is published before
 * that overlay lands, so without this every row would flash a misleading
 * "0/N never run" until the real statuses arrive.
 */
const AutomationSummaryCellSkeleton: React.FC<{ count: number }> = ({
  count,
}) => (
  <span className="flex items-center gap-1.5" aria-hidden="true">
    <Skeleton className="bg-coco-accent h-[18px] w-9 rounded-full" />
    <span className="flex items-center gap-px">
      {Array.from({ length: Math.max(count, 1) }, (_, i) => (
        <Skeleton key={i} className="bg-coco-accent h-3 w-1 rounded-[1px]" />
      ))}
    </span>
  </span>
);

/**
 * The Automations summary cell, wrapping {@link AutomationSummaryContent} with
 * a shimmer that shows until the per-row overlay has loaded at least once.
 * A component (rather than inline JSX in `renderCell`) so it can subscribe to
 * {@link DatasetAutomationsModel.readyWatchable}; only used when a
 * `readyWatchable` is threaded through, so the hook is always called.
 */
const AutomationSummaryCell: React.FC<{
  row: DatasetPreviewRow;
  workflows: readonly DatasetAutomationWorkflow[];
  readyWatchable: ReadonlyWatchable<boolean>;
}> = ({ row, workflows, readyWatchable }) => {
  const ready = useWatchableValue(readyWatchable);
  return ready ? (
    <AutomationSummaryContent row={row} workflows={workflows} />
  ) : (
    <AutomationSummaryCellSkeleton count={workflows.length} />
  );
};

/**
 * The empty Automations cell: a dashed pill once we know nothing is bound, but a
 * shimmer of that pill's shape while the wiring fetch is still in flight. Without
 * the shimmer a dataset that *does* have automations flashes "no automations"
 * down every row until the list lands — reading as blank rather than loading.
 */
const AutomationEmptyCell: React.FC<{
  settledWatchable: ReadonlyWatchable<boolean>;
}> = ({ settledWatchable }) => {
  const settled = useWatchableValue(settledWatchable);
  return settled ? (
    <AutomationEmptyPill />
  ) : (
    <Skeleton
      className="bg-coco-accent h-[18px] w-9 rounded-full"
      aria-hidden="true"
    />
  );
};

/** The wordless dashed pill shown when a dataset has no automations bound. */
const AutomationEmptyPill: React.FC = () => (
  <span
    title="No automations on this dataset"
    className="flex h-[18px] w-9 items-center justify-center rounded-full border border-dashed border-muted-foreground/25"
  >
    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/25" />
  </span>
);

export const summaryColumn = ({
  workflows,
  rowIdColumn,
  onRun,
  readyWatchable,
  settledWatchable,
  ownerOrgNameWatchable,
}: {
  workflows: readonly DatasetAutomationWorkflow[];
  /** Which column carries the row id the run controls need to name a row. */
  rowIdColumn: string;
  /**
   * Runs one workflow against one row, or every trigger on it when the ids are
   * omitted. Same handler the inline row panel uses.
   */
  onRun: (
    rowId: string,
    triggerId?: string,
    workflowId?: string
  ) => Promise<void>;
  /**
   * False until the per-row overlay has loaded. While false, every cell shows a
   * shimmer instead of the optimistic "never run" the empty overlay would read
   * as. Optional so callers that already have real data (tests, the header
   * stats path) need not thread it.
   */
  readyWatchable?: ReadonlyWatchable<boolean>;
  /**
   * False until the first wiring fetch settles. Gates the empty cell's shimmer,
   * so a dataset whose automations are still loading reads as loading rather
   * than as "no automations". Optional for the same reason as `readyWatchable`:
   * callers with data already in hand have nothing to wait on.
   */
  settledWatchable?: ReadonlyWatchable<boolean>;
  /**
   * The name of the org that owns the dataset, when it differs from the
   * reader's own. Threaded through to the row panel's locked-workflow
   * tooltip; optional for the same reason as the other watchables above.
   */
  ownerOrgNameWatchable?: ReadonlyWatchable<string | null>;
}): DataGridColumn<DatasetPreviewRow> => ({
  id: AUTOMATION_SUMMARY_COLUMN_ID,
  header: 'Automations',
  // Categorical, not numeric: the value is the row's rolled-up state, which is
  // what makes the column filterable by state and what the header marks filter
  // on. The count is still shown in the cell, read off the row.
  type: 'category',
  // Sized to the header's controls — the Run button and the column menu — now
  // that the name itself is a glyph rather than the word "Automations". The
  // column is pinned to the left edge, so every pixel it takes is one the
  // dataset's own columns never get back.
  width: 128,
  pinned: 'left',
  sortable: true,
  filterable: true,
  computed: true,
  // The workflow glyph in place of the word, because this column is pinned and
  // the header already carries two controls beside the name. `header` stays the
  // string, so the grid's own header tooltip — and the filter and display
  // dialogs, which take a label — still say "Automations"; the sr-only text
  // keeps the sort button announcing something other than its sort arrow.
  renderHeader: () => (
    // A flex box of its own: the grid drops this into a `truncate` span, which
    // would baseline-align a bare glyph against text that is no longer there.
    <span className="flex items-center">
      <Icon icon="workflow" size="small" className="shrink-0" />
      <span className="sr-only">Automations</span>
    </span>
  ),
  renderHeaderStats:
    workflows.length === 0
      ? // Nothing bound yet: there is no run distribution to break down, so the
        // header just points at the "Add automation" control beside it.
        () => (
          <span className="text-[10px] text-muted-foreground">
            No automations yet
          </span>
        )
      : (context) => (
          <StateBreakdown
            stats={context.stats}
            filterValue={context.filter?.value}
            onSelect={context.commands.toggleStatFilter}
          />
        ),
  // Null while nothing is bound, so the cell has no value to fall back to. The
  // rolled-up state of a row no workflow watches is `never`, which the grid
  // would otherwise print verbatim — and "never" reads as a run that has not
  // happened yet rather than as an automation that does not exist.
  getValue: (row) =>
    workflows.length === 0
      ? null
      : row[AUTOMATION_SUMMARY_COLUMN_ID] ?? 'never',
  renderCell: (context) => {
    // No workflows bound: an empty slot in the same pill shape the bound state
    // uses, so adding the first automation fills the shape in rather than
    // changing what the column looks like. Deliberately wordless — the header
    // already says "No automations yet", and repeating it down every row of the
    // grid says nothing the first cell did not.
    if (workflows.length === 0) {
      // While the wiring fetch is still in flight the empty state is a shimmer,
      // not the pill: this cell is `workflows.length === 0` both for a dataset
      // with no automations and for one whose list has not arrived yet, and the
      // pill would read as the former in a case that is really the latter.
      return settledWatchable === undefined ? (
        <AutomationEmptyPill />
      ) : (
        <AutomationEmptyCell settledWatchable={settledWatchable} />
      );
    }
    // No `readyWatchable` (tests, and any caller with real data in hand) means
    // there is nothing to wait on: render the content directly rather than a
    // cell that watches a watchable it does not have.
    return readyWatchable === undefined ? (
      <AutomationSummaryContent row={context.row} workflows={workflows} />
    ) : (
      <AutomationSummaryCell
        row={context.row}
        workflows={workflows}
        readyWatchable={readyWatchable}
      />
    );
  },
  // Expanding the cell opens the same per-workflow panel the row's own drawer
  // shows, rather than the grid's built-in value view. What sits behind this
  // cell is a rolled-up state code — `never`, `stale` — and a modal whose only
  // offer was to copy that word was the least useful thing an expanded
  // automations cell could have done: the reader wants to know *which*
  // workflow has not run, when it last did, and to start it.
  ...(workflows.length > 0 && {
    renderDetail: (context) => (
      <RowDetail
        row={context.row}
        workflows={workflows}
        rowIdColumn={rowIdColumn}
        onRun={onRun}
        ownerOrgNameWatchable={ownerOrgNameWatchable}
      />
    ),
  }),
});

/**
 * A real grid column showing one workflow's final-node output for each row —
 * what "expand this workflow into a column" produces.
 *
 * `index` is the workflow's position in the layer's workflow list, which is
 * what the projection keyed its output passenger by. The value rides on the row
 * rather than being a projected column, so the column is display-only: not
 * sortable or filterable, because there is no addressable SQL column behind it.
 */
export const workflowOutputColumn = (
  workflow: DatasetAutomationWorkflow,
  index: number
): DataGridColumn<DatasetPreviewRow> => ({
  id: workflowOutputGridColumnId(workflow.id),
  header: workflow.name,
  type: 'text',
  width: 220,
  computed: true,
  sortable: false,
  filterable: false,
  getValue: (row) => {
    const value = row[outputColumnId(index)];
    return typeof value === 'string' && value !== '' ? value : null;
  },
  renderCell: (context) => {
    const value = context.row[outputColumnId(index)];
    if (typeof value !== 'string' || value === '') {
      // An em dash rather than an empty cell, so "this workflow has not
      // produced an output for this row" reads as a state rather than a gap.
      return <span className="text-muted-foreground/40">—</span>;
    }
    return (
      <span className="block truncate" title={value}>
        {value}
      </span>
    );
  },
});

/**
 * States that mean "this workflow still owes this row a run".
 *
 * `failed` is deliberately absent. A failure is a distinct signal a user acts on
 * knowingly — the mockup counts it separately — and folding it in here would
 * re-run a permanently broken row on every click of a button that says "needs
 * run". Filtering to failed and running that is one click away either way.
 *
 * `cancelled` is present, and is the reason it is a state of its own. A stopped
 * run left the row unprocessed, so the batch a user cancelled is exactly what
 * "needs run" should pick up when they come back to finish it. Classified as
 * `failed` — which is what every cancelled run used to read as — those rows were
 * excluded from the one control that exists to re-run them.
 *
 * `pending` is absent for the same reason `running` is: the row has a run
 * coming, and a bulk control that picked it up would queue a second one for
 * work already asked for.
 */
const NEEDS_RUN_STATES: readonly AutomationState[] = [
  'never',
  'stale',
  'cancelled',
];

/**
 * A predicate over the projected per-workflow state columns, true for a row any
 * of `workflows` has yet to run successfully against current data.
 *
 * Built from the same column ids the join projects, so it selects exactly the
 * rows whose cells the user can see are outstanding. Empty when no workflow is
 * selected — `false` rather than an empty string, so a caller cannot accidentally
 * widen its query to everything.
 */
export const needsRunWhereSql = (
  workflows: readonly DatasetAutomationWorkflow[],
  selected: ReadonlySet<string>
): string => {
  const clauses = workflows
    .map((workflow, index) => ({ workflow, index }))
    .filter(({ workflow }) => selected.has(workflow.id))
    .map(
      ({ index }) =>
        `COALESCE(${duckDbIdentifier(
          stateColumnId(index)
        )}, 'never') IN (${NEEDS_RUN_STATES.map(duckDbStringLiteral).join(
          ', '
        )})`
    );
  return clauses.length === 0 ? 'false' : clauses.join(' OR ');
};

/** How each workflow stands across the rows the grid is showing. */
export type AutomationWorkflowTally = {
  workflowId: string;
  needsRun: number;
  failing: number;
  running: number;
  succeeded: number;
};

/**
 * Per-workflow counts over the grid's current source.
 *
 * One query that unpivots every workflow's state column rather than one query
 * per workflow: the join has already put all of them on each row, so this is a
 * single pass whatever the number of bound workflows.
 */
export const loadAutomationWorkflowTallies = async (
  /**
   * Runs one aggregate over whatever source the caller is describing. Taken as a
   * function rather than a SQL string so this works against the grid's fully
   * wrapped source — the only place a row with *no* run record is visible, which
   * is exactly the "never run" population these counts exist to report.
   */
  runAggregate: (selectSql: string) => Promise<Record<string, unknown>>,
  params: {
    workflows: readonly DatasetAutomationWorkflow[];
  }
): Promise<AutomationWorkflowTally[]> => {
  if (params.workflows.length === 0) {
    return [];
  }
  const counts = params.workflows.flatMap((_workflow, index) => {
    const state = `COALESCE(${duckDbIdentifier(
      stateColumnId(index)
    )}, 'never')`;
    return [
      `count(*) FILTER (WHERE ${state} IN (${NEEDS_RUN_STATES.map(
        duckDbStringLiteral
      ).join(', ')})) AS ${duckDbIdentifier(`needs_run_${index}`)}`,
      `count(*) FILTER (WHERE ${state} = 'failed') AS ${duckDbIdentifier(
        `failing_${index}`
      )}`,
      // A mark counts as running: the bulk panel is deciding what still has
      // work to do, and a run this tab has just asked for does not.
      `count(*) FILTER (WHERE ${state} IN ('running', 'pending')) AS ${duckDbIdentifier(
        `running_${index}`
      )}`,
      `count(*) FILTER (WHERE ${state} = 'succeeded') AS ${duckDbIdentifier(
        `succeeded_${index}`
      )}`,
    ];
  });

  const values = await runAggregate(counts.join(', '));
  const read = (key: string): number => Number(values[key] ?? 0);
  return params.workflows.map((workflow, index) => ({
    workflowId: workflow.id,
    needsRun: read(`needs_run_${index}`),
    failing: read(`failing_${index}`),
    running: read(`running_${index}`),
    succeeded: read(`succeeded_${index}`),
  }));
};

/** Marks the Run-all button while it is in flight. */
const RUN_ALL_KEY = '__all';

/**
 * The lock icon next to a workflow this reader cannot fire — bound to a
 * trigger owned by the org that shared the dataset.
 */
const LockedWorkflowTooltip: React.FC<{ ownerOrgName: string | null }> = ({
  ownerOrgName,
}) => (
  <Tooltip
    side="top"
    tooltipContent={
      ownerOrgName === null
        ? 'Runs in the org that shared this dataset'
        : `Runs in ${ownerOrgName}, which shared this dataset`
    }
  >
    <span className="flex h-7 w-7 shrink-0 items-center justify-center text-muted-foreground">
      <Icon icon="lock-closed" size="small" />
    </span>
  </Tooltip>
);

/** Reads the owner org name off its watchable before rendering the tooltip. */
const LockedWorkflowTooltipWatching: React.FC<{
  ownerOrgNameWatchable: ReadonlyWatchable<string | null>;
}> = ({ ownerOrgNameWatchable }) => (
  <LockedWorkflowTooltip
    ownerOrgName={useWatchableValue(ownerOrgNameWatchable)}
  />
);

/**
 * The trigger cadence shown beneath a workflow's name in the row panel — what
 * its dataset trigger watches for, plus a manual-only note when it never fires
 * on its own. This is the line that makes "why did this run?" answerable at a
 * glance: a workflow on `new_row` reads "A new row is added" rather than looking
 * like it runs on a schedule. Null when the criterion is unrecognised and the
 * trigger is automatic, so nothing misleading is shown.
 */
const automationCadenceLabel = (
  workflow: DatasetAutomationWorkflow
): string | null => {
  const watches =
    workflow.criterion !== null
      ? DATASET_TRIGGER_CRITERION_LABELS[workflow.criterion]
      : null;
  if (watches === null) {
    return workflow.runMode === 'manual' ? 'Only when I run it' : null;
  }
  return workflow.runMode === 'manual'
    ? `${watches} · only when I run it`
    : watches;
};

export const RowDetail: React.FC<{
  row: DatasetPreviewRow;
  workflows: readonly DatasetAutomationWorkflow[];
  rowIdColumn: string;
  /**
   * Runs one workflow, one whole trigger, or every trigger when both are
   * omitted.
   */
  onRun: (
    rowId: string,
    triggerId?: string,
    workflowId?: string
  ) => Promise<void>;
  /**
   * The name of the org that owns the dataset, when it differs from the
   * reader's own. Optional so a caller with no live data (tests) can omit it
   * and fall back to the generic "the org that shared it" phrasing.
   */
  ownerOrgNameWatchable?: ReadonlyWatchable<string | null>;
}> = ({ row, workflows, rowIdColumn, onRun, ownerOrgNameWatchable }) => {
  // Local, not lifted: this panel stays mounted for as long as its row is
  // expanded, and hoisting it would put transient UI state into the overlay,
  // whose identity rebuilds the whole grid view model.
  //
  // A set rather than one key: each button now fires only its own workflow, so
  // one in flight is no reason to disable the others — and with a single key
  // the first to finish would clear the label of any still running.
  const [busy, setBusy] = React.useState<ReadonlySet<string>>(new Set());
  const run = (key: string, triggerId?: string, workflowId?: string) => {
    setBusy((current) => new Set(current).add(key));
    void onRun(String(row[rowIdColumn]), triggerId, workflowId).finally(() =>
      setBusy((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      })
    );
  };
  const upToDate = Number(row[AUTOMATION_COUNT_COLUMN_ID] ?? 0);
  const rowId = row[rowIdColumn];
  // "Run all" fires every trigger on the row, so it is only honest when every
  // one of them is this reader's to fire — on a shared dataset some are not.
  const canRunAll = runnableWorkflows(workflows).length === workflows.length;
  return (
    <div className="flex h-full flex-col gap-2 overflow-y-auto px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {upToDate} of {workflows.length} workflows up to date
        </span>
        {/* Only worth offering when there is more than one thing to run;
            with a single workflow its own button already does the job. */}
        {canRunAll && workflows.length > 1 ? (
          <button
            type="button"
            disabled={
              busy.has(RUN_ALL_KEY) || rowId === null || rowId === undefined
            }
            className="rounded border border-border px-2 py-1 text-[11px] uppercase tracking-wide hover:bg-muted disabled:opacity-50"
            onClick={() => run(RUN_ALL_KEY)}
          >
            {busy.has(RUN_ALL_KEY) ? 'Running…' : 'Run all'}
          </button>
        ) : null}
      </div>
      {workflows.map((workflow, index) => {
        const state = readState(row, index);
        const ranAt = relativeTime(row[ranAtColumnId(index)]);
        const runId = readRowString(row, runIdColumnId(index));
        const cadenceLabel = automationCadenceLabel(workflow);
        return (
          <div
            key={workflow.id}
            className="flex items-center gap-3 rounded-lg border border-border/70 bg-background px-3 py-2"
          >
            <span
              className={cn(
                'h-2 w-2 shrink-0 rounded-full',
                STATE_PRESENTATION[state].dot
              )}
            />
            {/* Opens as a sibling artifact beside the dataset when this is in a
                chat, so a user checking what a workflow does keeps the row they
                were looking at. */}
            <div className="flex min-w-0 flex-1 flex-col">
              <ArtifactResourceLink
                to={{
                  resourceType: ResourceType.Workflow,
                  resourceId: workflow.id,
                  name: workflow.name,
                }}
                title="Open this workflow"
                className="truncate text-sm font-medium underline-offset-2 hover:underline"
              >
                {workflow.name}
              </ArtifactResourceLink>
              {cadenceLabel !== null ? (
                <span
                  className="truncate text-[11px] text-muted-foreground"
                  title="How often this automation runs"
                >
                  {cadenceLabel}
                </span>
              ) : null}
            </div>
            <StatePill state={state} />
            <span className="w-16 shrink-0 text-right text-xs text-muted-foreground">
              {ranAt ?? '—'}
            </span>
            {runId === null ? (
              <span className="w-28 shrink-0" />
            ) : (
              <ArtifactLink
                to={{
                  kind: 'workflow-run',
                  workflowId: workflow.id,
                  runId,
                }}
                title="Open this run"
                className="w-28 shrink-0 truncate text-right font-mono text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline cursor-pointer"
              >
                {runId}
              </ArtifactLink>
            )}
            {/* Not this reader's to start — the dataset was shared with them,
                the automation was not. Held open rather than collapsed so the
                rows above and below still line up. */}
            {!workflow.canRun ? (
              ownerOrgNameWatchable === undefined ? (
                <LockedWorkflowTooltip ownerOrgName={null} />
              ) : (
                <LockedWorkflowTooltipWatching
                  ownerOrgNameWatchable={ownerOrgNameWatchable}
                />
              )
            ) : (
              <Tooltip
                side="top"
                tooltipContent={
                  state === 'never' ? 'Run workflow' : 'Re-run workflow'
                }
              >
                <Button
                  type="button"
                  disabled={
                    busy.has(workflow.id) ||
                    rowId === null ||
                    rowId === undefined
                  }
                  size="icon-sm"
                  variant="outline"
                  loading={busy.has(workflow.id)}
                  icon={state === 'never' ? 'play' : 'repeat'}
                  onClick={() =>
                    run(workflow.id, workflow.triggerId, workflow.id)
                  }
                />
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );
};
